import { tr } from '../i18n';
import type { Lang } from '../store';
import type { ApiActiveResponse } from '../api';
import { photoUrl } from '../api';
import { shellHeader, shellFooter } from './shell';

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function formatDate(iso: string, lang: Lang): string {
  try {
    const d = new Date(iso + 'T00:00:00');
    return new Intl.DateTimeFormat(lang === 'ro' ? 'ro-RO' : 'en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    }).format(d);
  } catch { return iso; }
}

export function renderActive(lang: Lang, state: ApiActiveResponse): string {
  const L = tr(lang);
  const drop = state.active!;
  const top5 = state.top5 || [];
  const waitlistCount = state.waitlist_count ?? 0;
  const confirmed = state.confirmed_count ?? 0;
  const pending = state.pending_count ?? 0;
  const top5Open = state.top5_open ?? top5.length < drop.max_participants;
  const isClosed = drop.status === 'closed' || drop.status === 'completed';
  const max = drop.max_participants;

  const slots = Array.from({ length: max }, (_, i) => i + 1).map((n) => {
    const c = top5[n - 1];
    if (c) {
      const isConfirmed = c.status === 'confirmed';
      return /* html */ `
        <li class="sq-slot is-taken ${isConfirmed ? 'is-confirmed' : 'is-pending'}">
          <div class="sq-slot-n">${n}</div>
          <div class="sq-slot-photo">
            <img src="${esc(photoUrl(c.dog_photo_key))}" alt="${esc(c.dog_name)}" loading="lazy" />
          </div>
          <div class="sq-slot-info">
            <div class="sq-slot-name">${esc(c.dog_name)} <span class="sq-slot-breed">· ${esc(c.dog_breed)}</span></div>
            <div class="sq-slot-meta">${esc(c.instagram_handle)}</div>
          </div>
          <div class="sq-slot-status ${isConfirmed ? 'is-confirmed' : 'is-pending'}">
            ${isConfirmed
              ? `<span class="sq-status-icon">✓</span><span class="sq-status-label">${L.slot_confirmed}</span>`
              : `<span class="sq-status-icon">⏳</span><span class="sq-status-label">${L.slot_pending}</span>`
            }
          </div>
        </li>
      `;
    }
    return /* html */ `
      <li class="sq-slot is-empty">
        <div class="sq-slot-n">${n}</div>
        <div class="sq-slot-empty">${L.slot} ${n} — ${L.slot_available}</div>
      </li>
    `;
  }).join('');

  const hostLabel = (
    drop.host_account === 'sniffary' ? L.active_host_sniffary :
    drop.host_account === 'boopr' ? L.active_host_boopr :
    L.active_host_mash
  );

  let btnText = L.claim_button;
  let btnClass = '';
  let btnDisabled = false;
  if (isClosed) {
    btnText = L.claim_button_closed;
    btnClass = 'is-locked';
    btnDisabled = true;
  } else if (!top5Open) {
    btnText = L.claim_button_waitlist;
    btnClass = 'is-secondary';
  }

  return /* html */ `
    ${shellHeader(lang)}
    <main class="sq-active">
      <div class="sq-status-pill ${isClosed ? '' : 'is-active'}">
        <span class="sq-status-dot"></span>
        <span>${isClosed ? L.pill_full : L.pill_active}</span>
      </div>

      <div class="sq-active-headline">
        <div class="sq-kicker">${L.active_kicker}</div>
        <h1 class="sq-active-title">${L.active_questN}${drop.quest_number}</h1>
        <div class="sq-active-host">${hostLabel}</div>
      </div>

      <div class="sq-drop-meta">
        <div class="sq-meta-item">
          <div class="sq-meta-label">${L.active_field}</div>
          <div class="sq-meta-value">${esc(drop.field_location_name)}</div>
          ${drop.field_location_address ? `<div class="sq-meta-sub">${esc(drop.field_location_address)}</div>` : ''}
        </div>
        <div class="sq-meta-item">
          <div class="sq-meta-label">${L.active_date}</div>
          <div class="sq-meta-value">${formatDate(drop.quest_date, lang)}</div>
        </div>
        ${drop.quest_time_start ? `
        <div class="sq-meta-item">
          <div class="sq-meta-label">${L.active_time}</div>
          <div class="sq-meta-value">${esc(drop.quest_time_start)}</div>
        </div>` : ''}
      </div>

      <div class="sq-progress">
        <div class="sq-progress-count">
          <span class="sq-count-now">${top5.length}</span><span class="sq-count-max">/${max}</span>
        </div>
        <div class="sq-progress-label">${L.spots_taken}</div>
        <div class="sq-progress-bar">
          <span class="sq-progress-confirmed" style="width: ${(confirmed / max) * 100}%"></span>
          <span class="sq-progress-pending" style="width: ${(pending / max) * 100}%"></span>
        </div>
        ${waitlistCount > 0 ? `
          <div class="sq-waitlist-pill">
            <span class="sq-waitlist-num">+${waitlistCount}</span>
            <span class="sq-waitlist-label">${waitlistCount === 1 ? L.waitlist_one : L.waitlist_many}</span>
          </div>
        ` : ''}
      </div>

      <ul class="sq-slots">
        ${slots}
      </ul>

      ${!top5Open && !isClosed ? `<p class="sq-waitlist-explain">${L.waitlist_explain}</p>` : ''}

      <button class="sq-cta ${btnClass}" id="sq-claim-cta" ${btnDisabled ? 'disabled' : ''}>
        ${btnText}
      </button>

      ${isClosed ? `<p class="sq-full-note">${esc(L.full_note)}</p>` : ''}
    </main>
    ${shellFooter(lang)}
  `;
}
